/**
 * What the topbar bell reports.
 *
 * Alerts are derived from the same endpoints the dashboards read, so the bell
 * never claims something the screens contradict. Each alert names where to go,
 * because a count nobody can act on is noise.
 */
import type { Page } from "../routes";

export type AlertTone = "warn" | "info";

export type Alert = {
  key: string;
  label: string;
  detail: string;
  tone: AlertTone;
  /** Where the alert resolves. */
  to: { page: Page; id?: string | null; sub?: string | null };
};

export type AlertInputs = {
  /** Drawings whose parse needs a human decision. */
  reviewCount: number;
  /** Stock items under their minimum. */
  belowMin: number;
  /** Active machines carrying an hourly rate. */
  pricedMachines: number;
  machineCount: number;
  /** RFQ lines still undecided across open RFQs. */
  undecidedLines: number;
};

export function buildAlerts(input: AlertInputs): Alert[] {
  const alerts: Alert[] = [];

  if (input.reviewCount > 0) {
    alerts.push({
      key: "review",
      label: `${input.reviewCount} drawing${input.reviewCount === 1 ? "" : "s"} need review`,
      detail: "Parsed with low confidence — approve or reject before quoting.",
      tone: "warn",
      to: { page: "drawings" },
    });
  }

  if (input.belowMin > 0) {
    alerts.push({
      key: "stock",
      label: `${input.belowMin} SKU${input.belowMin === 1 ? "" : "s"} below minimum`,
      detail: "Reserve or raise a purchase order before releasing a work order.",
      tone: "warn",
      to: { page: "erp" },
    });
  }

  // No priced machine means every labour figure in the app reads zero.
  if (input.pricedMachines === 0) {
    alerts.push({
      key: "machines",
      label:
        input.machineCount === 0
          ? "No machines configured"
          : "No machine has an hourly rate",
      detail: "Labour cost stays at 0 ₺ until a machine carries a rate.",
      tone: "warn",
      to: { page: "erp" },
    });
  }

  if (input.undecidedLines > 0) {
    alerts.push({
      key: "rfq",
      label: `${input.undecidedLines} RFQ line${input.undecidedLines === 1 ? "" : "s"} undecided`,
      detail: "Each line needs a make or buy call before the quote is complete.",
      tone: "info",
      to: { page: "rfq" },
    });
  }

  return alerts;
}
